import React from 'react';
import BigNumber from 'bignumber.js';
import { Gauge, DollarSign, Shield, Percent } from 'lucide-react';
import { FOREX_PAIRS } from '../forex/config/pairs.ts';
import PocketField from './PocketField';
import PocketInstrument from './PocketInstrument';
import { getLiquidationBufferInfo, formatR } from '../../utils/liquidationBuffer';
import { PkRow, PkPnl, PkSection, PkHero, PkTwoCol, PkMainField, PkChipRow, PkChip, PkFees, PkFee, PkCols, PkCol, PkExits } from './PocketRows';
import { fmt, fmtLvl, fmtPips, money } from './pocketFormat';

const PAIR_OPTIONS = Object.values(FOREX_PAIRS).map((p) => ({ value: p.symbol, label: p.symbol, hint: p.name }));
const O = { decimalSeparator: '.', groupSeparator: ',', groupSize: 3 };

const lotTypeLabel = (lots) => {
  if (!(lots instanceof BigNumber) || lots.isNaN() || lots.isZero()) return '';
  const v = lots.toNumber();
  if (v >= 1) return '';
  if (v >= 0.1) { const c = lots.times(10).decimalPlaces(1); return ` ${c.toFormat(c.isInteger() ? 0 : 1, O)}mini`; }
  const c = lots.times(100).decimalPlaces(1); return ` ${c.toFormat(c.isInteger() ? 0 : 1, O)}micro`;
};
const fmtPipValue = (v) => !(v instanceof BigNumber) ? '--' : (v.lt(0.01) ? `$${v.toFormat(4, O)}` : `$${v.toFormat(2, O)}`);

// Forex market — cosmo layout. Mirrors ForexHeader + ForexMetricsCard calcs.
const PocketForex = ({ mode = 'compact', hook, onOpenSettings }) => {
  const { values, setValues, metrics, pairSpec } = hook;
  const full = mode === 'full';
  const setField = (field) => (bn) => setValues((prev) => ({ ...prev, [field]: bn }));

  const pl = metrics.priceLevels || {};
  const entry = values.entryPrice;
  const side = (entry instanceof BigNumber) && (values.stopLoss instanceof BigNumber)
    ? (values.stopLoss.gt(entry) ? 'short' : 'long') : null;
  const pricePrefix = pairSpec?.type === 'usd_quote' ? '$' : '';
  const decimals = pairSpec?.decimals ?? 5;

  const pip = (() => {
    if (!pairSpec || !(entry instanceof BigNumber) || !(metrics.quantity instanceof BigNumber)) return null;
    const pipSize = new BigNumber(pairSpec.pipSize);
    const q = metrics.quantity;
    const quote = pairSpec.symbol.split('/')[1];
    const pipValue = quote === 'USD' ? q.times(pipSize) : q.times(pipSize).dividedBy(entry);
    const dist = (lvl) => (lvl && lvl !== '--') ? new BigNumber(lvl).minus(entry).abs().dividedBy(pipSize) : null;
    return { pipValue, stopPips: dist(pl.stopLoss), tpPips: dist(pl.optimizedTakeProfit), bePips: dist(pl.breakEven), spreadPips: values.spread };
  })();

  const lotLabel = lotTypeLabel(metrics.lots);
  const showSwap = values.nights instanceof BigNumber && values.nights.gt(0) && metrics.feeBreakdown?.swapCost;

  const riskIcon = values.riskUnit === 'percent'
    ? <Percent size={12} className="text-[#6b7480]" />
    : <DollarSign size={12} className="text-[#6b7480]" />;
  const bufferInfo = getLiquidationBufferInfo({
    liquidationPrice: pl.liquidationPrice, stopLoss: values.stopLoss, entryPrice: entry,
    requestedR: values.safetyBufferR, maxLeverage: values.maxLeverage,
  });
  const bufTone = bufferInfo?.tone === 'danger' ? 'red' : bufferInfo?.tone === 'warn' ? 'amber' : undefined;

  return (
    <div className="pk-body">
      <PocketInstrument value={values.pair} options={PAIR_OPTIONS} sub={pairSpec?.name}
        onSelect={(p) => setValues((prev) => ({ ...prev, pair: p }))} />

      <PkTwoCol>
        <PkMainField label="Entry" copyValue={values.entryPrice}><PocketField value={values.entryPrice} onCommit={setField('entryPrice')} decimals={decimals} className="text-[12px]" /></PkMainField>
        <PkMainField label="Stop" copyValue={values.stopLoss}><PocketField value={values.stopLoss} onCommit={setField('stopLoss')} decimals={decimals} className="text-[12px]" /></PkMainField>
      </PkTwoCol>
      <PkChipRow>
        <PkChip icon={<Gauge size={12} className="text-[#6b7480]" />} title="Leverage"><PocketField value={values.maxLeverage} onCommit={setField('maxLeverage')} suffix="x" decimals={0} className="text-[12px]" /></PkChip>
        <PkChip icon={riskIcon} title="Risk"><PocketField value={values.risk} onCommit={setField('risk')} decimals={2} className="text-[12px]" /></PkChip>
        <PkChip label={values.multiTpEnabled ? "TPs" : "R:R"} title={values.multiTpEnabled ? "Multiple take-profits — set in Settings" : "Reward : Risk"}>{values.multiTpEnabled
          ? <span className="pk-mono text-[#eef3f8] text-[12px]">{values.tpCount}</span>
          : <PocketField value={values.nominalRR} onCommit={setField('nominalRR')} suffix="x" decimals={1} className="text-[12px]" />}</PkChip>
        <PkChip label="Sprd" title="Spread (pips)"><PocketField value={values.spread} onCommit={setField('spread')} suffix="p" decimals={1} className="text-[12px]" /></PkChip>
        <PkChip icon={<Shield size={12} className="text-[#6b7480]" />} title="Safety buffer"><PocketField value={values.safetyBufferR} onCommit={setField('safetyBufferR')} suffix="R" decimals={2} min={0.25} className="text-[12px]" /></PkChip>
      </PkChipRow>
      <PkChipRow>
        <PkChip label="Fee" title="Total round-turn fee — edit in Settings"><button onClick={onOpenSettings} className="pk-mono text-[#eef3f8] text-[12px] cursor-pointer hover:text-white transition-colors">{metrics.feeBreakdown?.totalRoundTurn ?? '--'}</button></PkChip>
      </PkChipRow>

      <div className="pk-sep" />

      <PkHero label="Position" side={side} value={money(metrics.positionSize)} sub={`Lot: ${fmt(metrics.lots)}${lotLabel}`} subLeft copyLabel="Position size" />
      <PkRow label="Leverage" value={`${fmt(metrics.leverage, { lev: true })}x`} sub={fmt(metrics.margin, { money: true })} subLeft copyLabel="Leverage" />
      <PkPnl profit={money(values.multiTpEnabled && metrics.multiExit ? metrics.multiExit.totalNetProfit : metrics.effectiveTake)} loss={money(metrics.effectiveStop)} />

      {values.multiTpEnabled && metrics.multiExit && <PkExits data={metrics.multiExit} mode={values.tpMode} pricePrefix={pricePrefix} decimals={decimals} />}

      <PkCols>
        <PkCol>
          <PkSection label="Trade" />
          {values.multiTpEnabled && metrics.multiExit ? <>
            <PkRow sm subLeft label="R:R" value={`${fmt(metrics.multiExit.blendedNetRR)}x`} sub="net" copyLabel="Blended net R:R" />
            <PkRow sm label="Avg exit" value={`${pricePrefix}${fmtLvl(metrics.multiExit.avgExitPrice, decimals)}`} copyLabel="Average exit" />
          </> : <>
            <PkRow sm subLeft label="R:R" value={`${fmt(metrics.optimizedRR)}x`} sub={`${fmt(metrics.nominalRR)}x`} copyLabel="Gross R:R" />
            {full && <PkRow sm subLeft label="Rookie" value={`${fmt(metrics.effectiveRR)}x`} sub={`${fmt(metrics.friction)}R`} copyLabel="Rookie R:R" />}
          </>}
          {full && bufferInfo && <PkRow sm label="Liq loss" value={`${formatR(bufferInfo.lossAtLiqR)}R`} tone={bufTone} info={bufferInfo.message} copyLabel="Loss at liquidation" />}
        </PkCol>
        <PkCol>
          <PkSection label="Levels" />
          <PkRow sm label="Liq" value={`${pricePrefix}${fmtLvl(pl.liquidationPrice, decimals)}`} tone="red" copyLabel="Liquidation" />
          <PkRow sm label="B/E" value={`${pricePrefix}${fmtLvl(pl.breakEven, decimals)}`} copyLabel="Break-even" />
          {!values.multiTpEnabled && <>
            <PkRow sm label="Nom TP" value={`${pricePrefix}${fmtLvl(pl.takeProfit, decimals)}`} copyLabel="Nominal TP" />
            <PkRow sm label="Opt TP" value={`${pricePrefix}${fmtLvl(pl.optimizedTakeProfit, decimals)}`} copyLabel="Optimized TP" />
          </>}
        </PkCol>
      </PkCols>

      {full && pip && <>
        <PkSection label="Pip info" />
        <PkFees cols={2}>
          <PkFee label="Pip $" value={fmtPipValue(pip.pipValue)} copyLabel="Pip value" />
          <PkFee label="B/E" value={`${fmtPips(pip.bePips)}p`} copyLabel="Break-even in pips" />
          <PkFee label="Stop" value={`${fmtPips(pip.stopPips)}p`} copyLabel="Stop in pips" />
          <PkFee label="Target" value={`${fmtPips(pip.tpPips)}p`} copyLabel="Target in pips" />
        </PkFees>
      </>}

      {full && <>
        <PkSection label="Fees" />
        <PkFees cols={3}>
          <PkFee label="Entry" value={fmt(metrics.tradingFees?.entryFee, { money: true })} copyLabel="Entry fee" />
          <PkFee label="TP" value={fmt(metrics.tradingFees?.tpFee, { money: true })} copyLabel="TP fee" />
          <PkFee label="SL" value={fmt(metrics.tradingFees?.slFee, { money: true })} copyLabel="SL fee" />
          {showSwap ? <PkFee label="Swap" value={String(metrics.feeBreakdown.swapCost)} copyLabel="Swap" /> : <span />}
          <PkFee label="Tot TP" value={fmt(metrics.totalFees?.takeProfitFees, { money: true })} copyLabel="Total TP" />
          <PkFee label="Tot SL" value={fmt(metrics.totalFees?.stopLossFees, { money: true })} copyLabel="Total SL" />
        </PkFees>
      </>}
    </div>
  );
};

export default PocketForex;
